import { HttpParams, HttpClient, HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';
import {Injectable} from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  AuthenticationOptionsResponseModel,
  EnrollRequestModel,
  InitiateEnrollmentRequestModel,
  FinishFidoRegistrationRequestModel,
  SetAuthMethodRequestModel,
  StartFidoRegistrationRequestModel,
  FingerScannerModel, FingerModel, FingerprintResponse
} from 'src/app/shared/models/enrollment.model';
import { NavbarService } from './navbar.service';
import { TranslateService } from "@ngx-translate/core";
import {StaticResourcesService} from "./static-resources.service";

@Injectable({
  providedIn: 'root'
})
export class EnrollmentService {

  endpoint = environment.API_URL + "rest/enrollment/";
  fingerScannerPort: number;
  fingerScannerBasePath: string = "http://localhost:19999";

  constructor(
    private http: HttpClient,
    private navbarService: NavbarService,
    public translate: TranslateService,
    private staticResourcesService: StaticResourcesService) {
    staticResourcesService.fetchConfigs().subscribe(
      configs => {
        this.fingerScannerBasePath = configs?.thalesConfigData?.basepath;
      },
      error => {
        console.log(error);
      }
    );
  }

  getFingerScanners() {
    return this.http
      .get<any>(this.fingerScannerBasePath + "/devices")
      .pipe(
        map(payload => {
          return payload.map( item => {
            return new FingerScannerModel(item.DeviceName, item.DeviceType, item.SerialNumber);
            }
          )
        })
      )
  }

  getFingerPrints() {
    return this.http
      .get<any>(this.fingerScannerBasePath + "/fingerprints/")
      .pipe(
        map(payload => {
          return payload.map( item => {
              return new FingerModel(item.ObjectID, item.ObjectName);
            }
          )
        })
      )
  }

  scanFingerprint(idx: number, format = "GreenBit") {
    var fmt: string;
    switch (format) {
      case "GreenBit":
        fmt = "GB_TEMPLATE";
        break;
      case "Bitmap":
        fmt = "BMP";
        break;
    }
    return this.http
      .get<FingerprintResponse[]>(this.fingerScannerBasePath + `/fingerprints/${idx}?outputFormats=${fmt}`)
  }

  initiateEnrollment(request: InitiateEnrollmentRequestModel) {
    return this.http
      .post<any>(
        this.endpoint + "initiate-enrollment",
          request,
          {
            headers: this.getHeaders()
          }
        )
      .pipe(
        map(responseData => {
          return responseData;
        })
      )
  }

  enroll(request: EnrollRequestModel) {
    return this.http
      .post<any>(
        this.endpoint + "enroll",
        request,
        { headers: this.getHeaders() }
      )
      .pipe(
        map(responseData => {
          return responseData;
        })
      )
  }

  restartStep(identity:string, enrollmentTrackerId:string) {
    let request: EnrollRequestModel = new EnrollRequestModel(null, identity, enrollmentTrackerId, null, null);
    return this.http
      .post<any>(
        this.endpoint + "restart-step",
        request,
        { headers:this.getHeaders() }
      )
      .pipe(
        map(responseData => {
          return responseData;
        })
      )
  }

  setAuthMethod(request: SetAuthMethodRequestModel) {
    return this.http
      .post<any>(
        this.endpoint + "authentication-method",
        request,
        { headers:this.getHeaders() }
      )
      .pipe(
        map(responseData => {
          return responseData;
        })
      )
  }

  continueEnrollment(authMethod:string, identity:string, formValue:any, enrollmentTrackerId:string, purpose: string) {
    let enrollmentData:any = formValue;
    let stringifiedEnrollmentData:string = this.stringify(enrollmentData);
    let req: EnrollRequestModel = new EnrollRequestModel(authMethod, identity, enrollmentTrackerId, stringifiedEnrollmentData, purpose);
    return (this.enroll(req));
  }

  continueSetAuthMethod(authMethod:string, identity:string, formValue:any, enrollmentTrackerId:string) {
    let data:any = formValue;
    let stringifiedData:string = this.stringify(data);
    let req: SetAuthMethodRequestModel = new SetAuthMethodRequestModel(authMethod, identity, enrollmentTrackerId, stringifiedData);
    return (this.setAuthMethod(req));
  }

  startFidoRegistration(sessionId: string) {
    let req: StartFidoRegistrationRequestModel = new StartFidoRegistrationRequestModel(sessionId);
    return this.http
      .post<any>(
        this.endpoint + "fido/attestation/options",
        req,
        { headers:this.getHeaders() }
      )
      .pipe(
        map(responseData => {
          return responseData;
        })
      )
  }

  finishFidoRegistration(deviceName: string, newDeviceName: string, useExistingDevice: boolean, sessionId: string, data: string, identity: string) {
    let req: FinishFidoRegistrationRequestModel = new FinishFidoRegistrationRequestModel(deviceName, newDeviceName, useExistingDevice, sessionId, data);
    let stringifiedData:string = this.stringify(req);
    let setAuthenticationMethodRequest: SetAuthMethodRequestModel = new SetAuthMethodRequestModel("FIDO", identity, sessionId, stringifiedData);
    return (this.setAuthMethod(setAuthenticationMethodRequest));
  }

  getFidoKeyNames(identityId: string) {
    let params = new HttpParams().set("identityId",identityId);
    return this.http
      .get<any>(
        this.endpoint + "fido/keys",
        {
          params: params,
          headers:this.getHeaders()
        }
      )
      .pipe(
        map(responseData => {
          return responseData;
        })
      )
  }

  createBiometricSession(requestObject: {config: string, nonce: string, type: string}) {
    return this.http
      .post<any>(
        this.endpoint + "createBioSession",
        requestObject,
        {
          headers:this.getHeaders()
        }
      )
      .pipe(
        map(responseData => {
          return responseData;
        })
      )
  }

  stringify(input:any){
    let result:any = "{}";
    if(input != null){
      Object.keys(input).forEach((key, index) => {
        let value:any = input[key];
        input[key] = value.toString();
      });
      result = JSON.stringify(input);
    }

    return result;
  }

  getHeaders() {
    const browserLang = this.translate.getBrowserLang();
    let language = undefined;
    if (this.navbarService.language) {
      language = this.navbarService.language;
    } else if (this.navbarService.identity && this.navbarService.identity.language) {
      language = this.navbarService.identity.language;
    }
    if (!language) {
      language = browserLang;
    }

    let headers = new HttpHeaders({
      'Accept-Language': language});

    return headers;
  }
}
